import * as React from 'react';
import {
  Button,
  Text,
  Spinner,
  Input,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
} from '@fluentui/react-components';
import {
  HomeRegular,
  FolderRegular,
  SlideTextRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../../auth/AuthContext';
import { Header } from '../../components/Header';
import { SyncStatusBar } from '../../components/SyncStatusBar';
import { ErrorBanner } from '../../components/ErrorBanner';
import { browseMessages } from '../../intl/messages';
import { useStyles } from './BrowseView.styles';
import { useBrowseListing } from './useBrowseListing';
import { useWorkspaceSearch } from './useWorkspaceSearch';
import { PresentationView } from '../PresentationView';
import type { FileItem, FolderItem } from './types';

interface BrowseViewProps {
  onBack: () => void;
}

export function BrowseView({ onBack }: BrowseViewProps) {
  const styles = useStyles();
  const intl = useIntl();
  const { api, accountName, accountId } = useAuth();
  const rootPath = accountName
    ? `/accounts/${accountName}/${accountName}`
    : null;

  const [search, setSearch] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState<FileItem | null>(null);

  const isSearchActive = search.trim().length > 0;

  const {
    rawItems,
    loading,
    error: listingError,
    breadcrumbs,
    openFolder,
    navigateToBreadcrumb,
  } = useBrowseListing({
    api,
    rootPath,
    onClearSearch: () => setSearch(''),
  });

  const { searchItems, searchLoading, searchError } = useWorkspaceSearch({
    api,
    accountId,
    rootPath,
    search,
  });

  const displayItems = isSearchActive ? searchItems : rawItems;
  const isDisplayLoading = isSearchActive ? searchLoading : loading;
  const error = listingError ?? searchError;

  if (selectedFile) {
    return (
      <PresentationView
        file={selectedFile}
        onBack={() => setSelectedFile(null)}
      />
    );
  }

  if (!rootPath) {
    const homeLabel = intl.formatMessage(browseMessages.home);
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.toolbar}>
            <Button
              icon={<HomeRegular />}
              appearance="subtle"
              size="small"
              onClick={onBack}
              title={homeLabel}
              aria-label={homeLabel}
            />
            <div className={styles.toolbarTitle}>
              <Text size={100} className={styles.toolbarEyebrow}>
                {intl.formatMessage(browseMessages.libraryTitle)}
              </Text>
              <Text className={styles.toolbarText}>
                {intl.formatMessage(browseMessages.browseTitle)}
              </Text>
            </div>
          </div>
          <div className={styles.centered}>
            <Text>{intl.formatMessage(browseMessages.workspaceError)}</Text>
          </div>
        </div>
      </div>
    );
  }

  const homeLabel = intl.formatMessage(browseMessages.home);
  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>
        <div className={styles.toolbar}>
          <Button
            icon={<HomeRegular />}
            appearance="subtle"
            size="small"
            onClick={onBack}
            title={homeLabel}
            aria-label={homeLabel}
          />
          <div className={styles.toolbarTitle}>
            <Text size={100} className={styles.toolbarEyebrow}>
              {intl.formatMessage(browseMessages.libraryTitle)}
            </Text>
            <Text className={styles.toolbarText}>
              {intl.formatMessage(browseMessages.browseTitle)}
            </Text>
          </div>
        </div>

        <div className={styles.breadcrumbRow}>
          <Breadcrumb size="small">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <BreadcrumbDivider />}
                <BreadcrumbItem>
                  <BreadcrumbButton
                    current={index === breadcrumbs.length - 1}
                    onClick={() => navigateToBreadcrumb(index)}
                  >
                    {crumb.name}
                  </BreadcrumbButton>
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </Breadcrumb>
        </div>

        <div className={styles.searchRow}>
          <Input
            contentBefore={<SearchRegular />}
            placeholder={intl.formatMessage(browseMessages.searchPlaceholder)}
            value={search}
            onChange={(_, data) => setSearch(data.value)}
            size="small"
            style={{ width: '100%' }}
          />
        </div>

        {error && <ErrorBanner message={error} />}

        <div className={styles.list}>
          {isDisplayLoading && (
            <div className={styles.centered}>
              <Spinner
                size="medium"
                label={intl.formatMessage(browseMessages.loading)}
              />
            </div>
          )}

          {!isDisplayLoading && displayItems.length === 0 && (
            <div className={styles.centered}>
              <Text>{intl.formatMessage(browseMessages.noResults)}</Text>
            </div>
          )}

          {!isDisplayLoading &&
            displayItems.map(item =>
              item.type === 'directory' ? (
                <FolderRow
                  key={item.id}
                  item={item}
                  onOpen={openFolder}
                  styles={styles}
                />
              ) : (
                <FileRow
                  key={item.id}
                  item={item}
                  isSearchActive={isSearchActive}
                  onSelect={setSelectedFile}
                  styles={styles}
                  intl={intl}
                />
              )
            )}
        </div>
      </div>
      <SyncStatusBar />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row sub-components — kept in this file because they are simple, view-local,
// and only used here. Extract if reused elsewhere.
// ---------------------------------------------------------------------------

function FolderRow({
  item,
  onOpen,
  styles,
}: {
  item: FolderItem;
  onOpen: (folder: FolderItem) => void;
  styles: ReturnType<typeof useStyles>;
}) {
  return (
    <div
      className={styles.itemRow}
      onClick={() => onOpen(item)}
      role="button"
      tabIndex={0}
      aria-label={item.name}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
    >
      <FolderRegular fontSize={20} />
      <Text className={styles.itemName}>{item.name}</Text>
    </div>
  );
}

function FileRow({
  item,
  isSearchActive,
  onSelect,
  styles,
  intl,
}: {
  item: FileItem;
  isSearchActive: boolean;
  onSelect: (file: FileItem) => void;
  styles: ReturnType<typeof useStyles>;
  intl: ReturnType<typeof useIntl>;
}) {
  const pathLabel = isSearchActive ? item.pathLabel ?? '' : '';

  return (
    <div
      className={styles.itemRow}
      onClick={() => onSelect(item)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(item);
        }
      }}
      aria-label={intl.formatMessage(browseMessages.openItem, {
        title: item.title,
      })}
    >
      {item.thumbnailUrl ? (
        <img
          src={item.thumbnailUrl}
          alt={intl.formatMessage(browseMessages.thumbnailAlt, {
            title: item.title,
          })}
          className={styles.thumbnail}
        />
      ) : (
        <SlideTextRegular fontSize={20} />
      )}
      <div className={styles.itemTextGroup}>
        <Text className={styles.itemName}>{item.title}</Text>
        {pathLabel && <Text className={styles.itemPath}>{pathLabel}</Text>}
      </div>
    </div>
  );
}
